import {
  CRUD,
  PERMISSION_AREAS,
  PERMISSIONS,
  RoleInput,
  type Crud,
  type PermissionArea,
  type PermissionDef,
  type RolesBook,
  type RoleView,
} from '@cyberscore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, Check, Copy, Pencil, Plus, ShieldCheck, Trash2, X } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../../lib/api';
import { refreshLists } from '../../lib/list';
import { href } from '../../lib/router';
import { can, useSession } from '../../lib/session';
import { useToast } from '../../ui/Toasts';

const COLUMN: Record<Crud, string> = { view: 'View', create: 'Create', edit: 'Edit', delete: 'Delete' };
const failure = (e: unknown) =>
  e instanceof ApiError ? (e.issues[0]?.message ?? e.message) : 'Something went wrong';

/** The permissions in one cell of the matrix: one, or one and its wider "every record" version. */
const cell = (area: string, action: Crud, available: ReadonlySet<string>) =>
  PERMISSIONS.filter((p) => p.area === area && p.action === action && available.has(p.key));
const others = (area: string, available: ReadonlySet<string>) =>
  PERMISSIONS.filter((p) => p.area === area && p.action === 'other' && available.has(p.key));

function useRoles() {
  const { tenant } = useSession();
  return useQuery({ queryKey: ['roles', tenant?.id], queryFn: () => api<RolesBook>('GET', '/tenants/current/roles'), enabled: !!tenant }); // prettier-ignore
}

function useRoleAction() {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: (
      a:
        | { type: 'create'; body: RoleInput }
        | { type: 'update'; key: string; body: Partial<RoleInput> }
        | { type: 'remove'; key: string },
    ) =>
      // prettier-ignore
      a.type === 'create'
        ? api<RolesBook>('POST', '/tenants/current/roles', a.body)
        : a.type === 'update'
          ? api<RolesBook>('PATCH', `/tenants/current/roles/${a.key}`, a.body)
          : api<RolesBook>('DELETE', `/tenants/current/roles/${a.key}`),
    onSuccess: (book) => {
      qc.setQueryData(['roles', tenant?.id], book);
      // The People page offers the roles too.
      void refreshLists(qc);
    },
  });
}

interface Draft {
  key: string | null;
  name: string;
  description: string;
  permissions: Set<string>;
}

/**
 * Roles and permissions (task 78, decision 0055): every role this organisation hands out as a matrix of
 * what it may view, create, edit and delete, and other actions. Built-in roles are fixed; administrators
 * add their own, starting empty or from a copy, and can only give what they may do themselves.
 * `embedded`: inside Tenant 360's tab (task 102), under a smaller heading.
 */
export function RolesPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { tenant } = useSession();
  const roles = useRoles();
  const act = useRoleAction();
  const toast = useToast();
  const [tab, setTab] = useState<'roles' | 'compare'>('roles');
  const [open, setOpen] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);

  if (roles.isPending) return <p className="muted">Loading…</p>;
  if (roles.isError)
    return (
      <p className="error" role="alert">
        {failure(roles.error)}
      </p>
    );
  const book = roles.data;
  const available = new Set(book.available);
  const mine = new Set(book.mine);
  const creates = can(tenant, 'roles.create');
  const edits = can(tenant, 'roles.edit');
  const removes = can(tenant, 'roles.delete');
  const shown = book.roles.find((r) => r.key === open) ?? null;
  // A copy keeps only what the person copying may give.
  const copy = (r: RoleView) => { setDraft({ key: null, name: `${r.name} (copy)`.slice(0, 60), description: r.description ?? '', permissions: new Set(r.permissions.filter((p) => mine.has(p))) }); setOpen(null); }; // prettier-ignore
  const remove = (r: RoleView) =>
    act.mutate({ type: 'remove', key: r.key }, { onSuccess: () => { toast(`Removed ${r.name}`, 'ok'); setRemoving(null); if (open === r.key) setOpen(null); }, onError: (e) => { toast(failure(e), 'bad'); setRemoving(null); } }); // prettier-ignore

  return (
    <>
      <div className={`page-head row-between wrap gap${embedded ? ' embedded' : ''}`}>
        <div>
          {embedded ? <h2>Roles</h2> : <h1>Roles</h1>}
          <p>What each role may do, as the API and the database enforce it. Built-in roles are fixed; add your own from any mix of permissions, or start from a copy. Someone with several roles can do everything each of them can.</p>
        </div>
        {creates && !draft && <button type="button" className="btn" onClick={() => { setDraft({ key: null, name: '', description: '', permissions: new Set() }); setOpen(null); }}><Plus size={15} /> New role</button>}
      </div>

      {(() => {
        // Say first whether the built-in roles are all that is in use (task 113, decision 0074).
        const own = book.roles.filter((r) => !r.built_in).length;
        const builtIn = book.roles.length - own;
        return (
          <section className={`card pad recommended mb${own ? '' : ' ok'}`} aria-label="Roles in use">
            <h2 className="section-title row gap">{own ? <ShieldCheck size={16} /> : <BadgeCheck size={16} />}{own ? `${builtIn} built-in roles and ${own} of your own` : 'Using recommended settings'}</h2>
            <p className="muted small">{own ? 'Your own roles sit beside the built-in ones; people can hold several.' : `The ${builtIn} built-in roles cover the usual jobs, and are kept up to date as the platform grows. Make your own only if you need a different mix: start from a copy of the closest one.`}</p>
          </section>
        );
      })()}

      {draft && <Editor key={draft.key ?? 'new'} draft={draft} available={available} mine={mine} onDone={() => setDraft(null)} />}

      <div className="seg list-tabs" role="group" aria-label="Show">
        <button type="button" aria-pressed={tab === 'roles'} onClick={() => setTab('roles')}>Roles</button>
        <button type="button" aria-pressed={tab === 'compare'} onClick={() => setTab('compare')}>Compare roles</button>
      </div>

      {tab === 'roles' ? (
        <>
          <section className="card" aria-label="Roles">
            <div className="table-wrap">
              <table className="grid roles-table">
                <thead><tr><th>Role</th><th className="num">People</th><th className="num wide">Permissions</th><th /></tr></thead>
                <tbody>
                  {book.roles.map((r) => (
                    <tr key={r.key} className={open === r.key ? 'selected' : undefined}>
                      <td>
                        <button type="button" className="linklike" onClick={() => setOpen(open === r.key ? null : r.key)} aria-expanded={open === r.key}>{r.name}</button>
                        {r.built_in ? <span className="pill neutral small-pill">Built-in</span> : <span className="pill ok small-pill">Your own</span>}
                        {r.description && <div className="muted small">{r.description}</div>}
                      </td>
                      <td className="num">{r.members}{r.invitations ? <span className="muted small"> +{r.invitations} invited</span> : null}</td>
                      <td className="num wide">{r.permissions.length}</td>
                      <td className="num">
                        <div className="row gap-s end">
                          {creates && <button type="button" className="btn btn-sm btn-ghost" aria-label={`Copy ${r.name}`} title="Copy" onClick={() => copy(r)}><Copy size={14} /></button>}
                          {!r.built_in && edits && <button type="button" className="btn btn-sm btn-ghost" aria-label={`Change ${r.name}`} title="Change" onClick={() => { setDraft({ key: r.key, name: r.name, description: r.description ?? '', permissions: new Set(r.permissions) }); setOpen(null); }}><Pencil size={14} /></button>}
                          {!r.built_in && removes && (removing === r.key ? (
                            <>
                              <button type="button" className="btn btn-sm btn-danger" disabled={act.isPending} onClick={() => remove(r)}>Remove</button>
                              <button type="button" className="btn btn-sm btn-ghost" aria-label="Keep it" onClick={() => setRemoving(null)}><X size={14} /></button>
                            </>
                          ) : (
                            <button type="button" className="btn btn-sm btn-ghost" aria-label={`Remove ${r.name}`} title="Remove" onClick={() => setRemoving(r.key)}><Trash2 size={14} /></button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          {shown && (
            <section className="card mt" aria-labelledby="role-h">
              <div className="card-head row-between">
                <h2 id="role-h" className="card-title row gap"><ShieldCheck size={16} /> {shown.name}</h2>
                <span className="muted small">{shown.built_in ? 'Built-in: copy it to make a role of your own' : `${shown.permissions.length} permissions`}</span>
              </div>
              <Matrix available={available} value={new Set(shown.permissions)} />
            </section>
          )}
        </>
      ) : (
        <Compare book={book} available={available} />
      )}
      <p className="muted small mt">Roles are given to people on the <a href={href.people()}>People</a> page.</p>
    </>
  ); // prettier-ignore
}

/** A new role, or changes to one of the organisation's own. */
function Editor({
  draft,
  available,
  mine,
  onDone,
}: {
  draft: Draft;
  available: ReadonlySet<string>;
  mine: ReadonlySet<string>;
  onDone: () => void;
}) {
  const act = useRoleAction();
  const toast = useToast();
  const [name, setName] = useState(draft.name);
  const [description, setDescription] = useState(draft.description);
  const [perms, setPerms] = useState(draft.permissions);
  const [error, setError] = useState<string | null>(null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const body = RoleInput.safeParse({
      name,
      description: description.trim() || null,
      permissions: [...perms],
    });
    if (!body.success) return setError(body.error.issues[0]!.message);
    setError(null);
    const done = { onSuccess: () => { toast(draft.key ? `Saved ${body.data.name}` : `Added ${body.data.name}`, 'ok'); onDone(); }, onError: (err: Error) => setError(failure(err)) }; // prettier-ignore
    if (draft.key) act.mutate({ type: 'update', key: draft.key, body: body.data }, done);
    else act.mutate({ type: 'create', body: body.data }, done);
  };
  return (
    <form className="card form-card editing mb" onSubmit={submit} noValidate aria-label={draft.key ? `Change ${draft.name}` : 'New role'}>
      <div className="card-head"><div className="card-title">{draft.key ? `Change ${draft.name}` : 'New role'}</div></div>
      <div className="form-body">
        <div className="field">
          <label htmlFor="role-name">Name</label>
          <input id="role-name" className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder="Claims handler" />
        </div>
        <div className="field">
          <label htmlFor="role-desc">What it’s for <span className="muted">(optional)</span></label>
          <input id="role-desc" className="input" value={description} maxLength={300} onChange={(e) => setDescription(e.target.value)} placeholder="Reads every check and its review; changes nothing" />
        </div>
        <span className="field-label">Permissions · {perms.size}</span>
      </div>
      <Matrix available={available} value={perms} mine={mine} onChange={setPerms} />
      {error && <div className="error mx" role="alert">{error}</div>}
      <div className="form-foot">
        <button type="submit" className="btn btn-primary" disabled={act.isPending}>{draft.key ? 'Save role' : 'Add role'}</button>
        <button type="button" className="btn btn-ghost" onClick={onDone}>Cancel</button>
      </div>
    </form>
  ); // prettier-ignore
}

/** One role's permissions: areas down the side, view, create, edit and delete across, other actions under each. */
function Matrix({
  available,
  value,
  mine,
  onChange,
}: {
  available: ReadonlySet<string>;
  value: ReadonlySet<string>;
  mine?: ReadonlySet<string>;
  onChange?: (v: Set<string>) => void;
}) {
  const editing = !!onChange;
  const groups = [...new Set(PERMISSION_AREAS.map((a) => a.group))];
  const areasOf = (g: PermissionArea['group']) => PERMISSION_AREAS.filter((a) => a.group === g && PERMISSIONS.some((p) => p.area === a.key && available.has(p.key))); // prettier-ignore
  const set = (on: string[], off: string[]) => {
    const next = new Set(value);
    for (const p of off) next.delete(p);
    for (const p of on) next.add(p);
    onChange?.(next);
  };
  const allowed = (p: PermissionDef) => !mine || mine.has(p.key);
  const areaPerms = (a: string) => PERMISSIONS.filter((p) => p.area === a && available.has(p.key));

  return (
    <div className="table-wrap">
      <table className="grid perm-matrix">
        <thead>
          <tr><th>Area</th>{CRUD.map((c) => <th key={c} className="c">{COLUMN[c]}</th>)}</tr>
        </thead>
        {groups.map((g) => {
          const areas = areasOf(g);
          if (!areas.length) return null;
          return (
            <tbody key={g}>
              <tr className="perm-group"><th colSpan={5} scope="colgroup">{g}</th></tr>
              {areas.map((a) => {
                const extra = others(a.key, available);
                const every = areaPerms(a.key).filter(allowed);
                const full = every.length > 0 && every.every((p) => value.has(p.key));
                return [
                  <tr key={a.key} className={extra.length ? 'has-other' : undefined}>
                    <th scope="row" className="perm-area">
                      <span className="perm-area-label">
                        {editing && every.length > 0 && <input type="checkbox" aria-label={`Everything in ${a.label}`} title="Everything in this area" checked={full} onChange={(e) => set(e.target.checked ? every.map((p) => p.key) : [], e.target.checked ? [] : every.map((p) => p.key))} />}
                        <span>{a.label}<span className="hint block">{a.hint}</span></span>
                      </span>
                    </th>
                    {CRUD.map((c) => <td key={c} className="c"><Cell perms={cell(a.key, c, available)} area={a.label} column={COLUMN[c]} value={value} allowed={allowed} set={editing ? set : null} /></td>)}
                  </tr>,
                  extra.length > 0 && (
                    <tr key={`${a.key}-other`} className="perm-other">
                      <td colSpan={5}>
                        <span className="muted small">Also: </span>
                        {extra.map((p) => editing ? (
                          <label key={p.key} className={`perm-chip${allowed(p) ? '' : ' off'}`} title={allowed(p) ? undefined : 'You can’t give what you can’t do yourself'}>
                            <input type="checkbox" checked={value.has(p.key)} disabled={!allowed(p)} onChange={(e) => set(e.target.checked ? [p.key] : [], e.target.checked ? [] : [p.key])} /> {p.label}
                          </label>
                        ) : (
                          <span key={p.key} className={`perm-chip${value.has(p.key) ? ' on' : ' off'}`}>{value.has(p.key) ? <Check size={12} aria-label="Yes" /> : <X size={12} aria-label="No" />} {p.label}</span>
                        ))}
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          );
        })}
      </table>
    </div>
  ); // prettier-ignore
}

/** One cell: nothing, a single permission, or "their own" and "every one" of something. */
function Cell({
  perms,
  area,
  column,
  value,
  allowed,
  set,
}: {
  perms: PermissionDef[];
  area: string;
  column: string;
  value: ReadonlySet<string>;
  allowed: (p: PermissionDef) => boolean;
  set: ((on: string[], off: string[]) => void) | null;
}) {
  if (!perms.length)
    return (
      <span className="muted" aria-hidden="true">
        ·
      </span>
    );
  const narrow = perms.find((p) => !p.widens)!;
  const wide = perms.find((p) => p.widens);
  const level = wide && value.has(wide.key) ? 'all' : value.has(narrow.key) ? (wide ? 'own' : 'yes') : 'none';
  const label = `${area}: ${column}`;
  if (!set) {
    if (level === 'none')
      return (
        <span className="muted" aria-label="No">
          –
        </span>
      );
    return level === 'yes' ? <Check size={15} className="ok-t" aria-label={narrow.label} /> : <span className="pill ok small-pill" title={level === 'all' ? wide!.label : narrow.label}>{level === 'all' ? 'All' : 'Own'}</span>; // prettier-ignore
  }
  if (!wide)
    return <input type="checkbox" aria-label={label} title={narrow.label} checked={level === 'yes'} disabled={!allowed(narrow)} onChange={(e) => set(e.target.checked ? [narrow.key] : [], e.target.checked ? [] : [narrow.key])} />; // prettier-ignore
  return (
    <select className="input perm-scope" aria-label={label} title={`${narrow.label}; or ${wide.label.toLowerCase()}`} value={level} onChange={(e) => {
      const v = e.target.value;
      set(v === 'all' ? [narrow.key, wide.key] : v === 'own' ? [narrow.key] : [], v === 'all' ? [] : v === 'own' ? [wide.key] : [narrow.key, wide.key]);
    }}>
      <option value="none">–</option>
      <option value="own" disabled={!allowed(narrow)}>Own</option>
      <option value="all" disabled={!allowed(wide)}>All</option>
    </select>
  ); // prettier-ignore
}

/** Every role side by side: a row per permission, a column per role. */
function Compare({ book, available }: { book: RolesBook; available: ReadonlySet<string> }) {
  const rows = PERMISSIONS.filter((p) => available.has(p.key));
  return (
    <section className="card" aria-label="Compare roles">
      <div className="table-wrap">
        <table className="grid role-matrix compare-matrix">
          <thead><tr><th>Permission</th>{book.roles.map((r) => <th key={r.key} className="c">{r.name}</th>)}</tr></thead>
          <tbody>
            {rows.map((p, i) => {
              const area = PERMISSION_AREAS.find((a) => a.key === p.area)!;
              const first = rows[i - 1]?.area !== p.area;
              return [
                first && <tr key={`${p.area}-h`} className="perm-group"><th colSpan={book.roles.length + 1} scope="colgroup">{area.label}</th></tr>,
                <tr key={p.key}>
                  <th scope="row" className="perm-name">{p.label}</th>
                  {book.roles.map((r) => (
                    <td key={r.key} className="c">{r.permissions.includes(p.key) ? <Check size={15} aria-label="Yes" className="ok-t" /> : <span className="muted" aria-label="No">–</span>}</td>
                  ))}
                </tr>,
              ];
            })}
          </tbody>
        </table>
      </div>
    </section>
  ); // prettier-ignore
}
